import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { blogWithdrawal } from '../config/DAOIntegration';
import { isInsufficientNativeGasError, requestGasDrip } from '../config/gasDrip';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { 
  Search, 
  Plus,
  Edit,
  Trash2,
  Eye,
  FileText,
  DollarSign,
  TrendingUp,
  Users,
  BookOpen,
  Loader2,
  Calendar,
  BarChart3
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BLOG_CATEGORIES = [
  'Recruiting',
  'News',
  'Sport',
  'Business',
  'Innovation',
  'Health',
  'Culture',
  'Arts',
  'Travel',
  'Earth',
  'Technology',
  'Education',
  'Entertainment'
];

const BloggerPage = () => {
  const { user } = useAuth();
  const { isConnected, signer, connectWallet, provider } = useWeb3();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('blogs');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, blogId: null });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  // Fetch user's blogs
  const { data: blogsData, isLoading: blogsLoading } = useQuery(
    ['myBlogs', statusFilter, searchQuery],
    () => api.get(API_ENDPOINTS.BLOGS.MY_BLOGS, {
      params: {
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        page: 1,
        limit: 50
      }
    }),
    {
      select: (data) => data.data,
      enabled: !!user,
    }
  );

  // Fetch earnings
  const { data: earningsData, isLoading: earningsLoading } = useQuery(
    ['blogEarnings'],
    () => api.get(API_ENDPOINTS.BLOGS.EARNINGS),
    {
      select: (data) => data.data,
      enabled: !!user && activeTab === 'earnings',
    }
  );

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['blogStats'],
    () => api.get(API_ENDPOINTS.BLOGS.STATS),
    {
      select: (data) => data.data,
      enabled: !!user && activeTab === 'stats',
    }
  );

  // Delete blog mutation
  const deleteBlogMutation = useMutation(
    (blogId) => api.delete(API_ENDPOINTS.BLOGS.DELETE(blogId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['myBlogs']);
        toast.success('Blog deleted successfully');
        setDeleteModal({ isOpen: false, blogId: null });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete blog');
      }
    }
  );

  // Helper function to ensure wallet connection
  const ensureWalletConnection = async () => {
    if (isConnected && signer) {
      return true;
    }
    const result = await connectWallet();
    if (result && result.address) {
      return true;
    }
    return false;
  };

  // Helper function to resolve signer
  const resolveSigner = async () => {
    if (signer) return signer;
    if (provider) {
      try {
        return await provider.getSigner();
      } catch (error) {
        console.error('Unable to resolve signer from provider', error);
      }
    }
    return null;
  };

  // Withdraw mutation (now uses contract signing)
  const withdrawMutation = useMutation(
    async (amount) => {
      // Ensure wallet is connected and we have a valid signer
      const connected = await ensureWalletConnection();
      if (!connected) {
        throw new Error('Please connect your wallet to withdraw blog earnings');
      }
      
      // Resolve signer
      const signerInstance = await resolveSigner();
      if (!signerInstance) {
        throw new Error('Unable to access wallet signer. Please try reconnecting your wallet.');
      }
      
      const userWallet = await signerInstance.getAddress();

      // Call DAO contract blogWithdrawal (if no gas, drip then retry once)
      let result;
      try {
        result = await blogWithdrawal(signerInstance, amount.toString());
      } catch (e) {
        if (!isInsufficientNativeGasError(e)) throw e;
        await requestGasDrip(userWallet);
        result = await blogWithdrawal(signerInstance, amount.toString());
      }
      // Also update backend with transaction hash
      await api.post(API_ENDPOINTS.BLOGS.WITHDRAW, { 
        amount: parseFloat(amount),
        txHash: result.txHash
      });
      return { amount: parseFloat(amount), txHash: result.txHash };
    },
    {
      onSuccess: (response) => {
        toast.success(`Successfully withdrew ${response.amount} LOB tokens!`);
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        queryClient.invalidateQueries(['blogEarnings']);
        queryClient.invalidateQueries(['blogStats']);
        queryClient.invalidateQueries('user');
      },
      onError: (error) => {
        if (error.message?.includes('connect your wallet')) {
          toast.error('Please connect your wallet to withdraw');
        } else {
          toast.error(error.response?.data?.error || error.message || 'Failed to withdraw LOB tokens');
        }
      }
    }
  );

  const blogs = blogsData?.blogs || [];
  const earnings = earningsData || {};
  const stats = statsData || {};

  const handleDeleteBlog = (blogId) => {
    setDeleteModal({ isOpen: true, blogId });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.blogId) {
      deleteBlogMutation.mutate(deleteModal.blogId);
    }
  };

  const handleWithdraw = () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (parseFloat(withdrawAmount) > (earnings.available || 0)) {
      toast.error('Insufficient available balance');
      return;
    }
    withdrawMutation.mutate(withdrawAmount);
  };

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Please log in to view your blogs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <BookOpen className="w-8 h-8 mr-3 text-accent" />
              Blogger Dashboard
            </h1>
            <Link
              to="/create-blog"
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Blog</span>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('blogs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'blogs'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              My Blogs
            </button>
            <button
              onClick={() => setActiveTab('earnings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'earnings'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Earnings
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Statistics
            </button>
          </nav>
        </div>

        {/* Blogs Tab */}
        {activeTab === 'blogs' && (
          <div>
            {/* Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search blogs..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* Blogs List */}
            {blogsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : blogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No blogs yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Start creating your first blog post!
                </p>
                <Link to="/create-blog" className="btn-primary inline-flex items-center space-x-2">
                  <Plus className="w-5 h-5" />
                  <span>Create Blog</span>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4">
                {blogs.map((blog) => (
                  <div
                    key={blog._id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {blog.title}
                          </h3>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              blog.status === 'published'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {blog.status}
                          </span>
                        </div>
                        {blog.excerpt && (
                          <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                            {blog.excerpt}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            {blog.category}
                          </span>
                          <span className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {blog.views || 0} views
                          </span>
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDistanceToNow(new Date(blog.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {blog.status === 'published' && (
                          <a
                            href={`http://localhost:3001/blogs/${blog.slug || blog._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                            title="View"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        )}
                        <button
                          onClick={() => navigate(`/edit-blog/${blog._id}`)}
                          className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteBlog(blog._id)}
                          className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === 'earnings' && (
          <div>
            {earningsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Earnings Rates Display */}
                {earnings.config && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                      Current Earning Rates
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-800 dark:text-blue-300">
                      <div>
                        <span className="font-medium">{earnings.config.viewsThreshold} views</span> = <span className="font-semibold">{earnings.config.viewsRate} LOB</span>
                      </div>
                      <div>
                        <span className="font-medium">{earnings.config.impressionsThreshold} impressions</span> = <span className="font-semibold">{earnings.config.impressionsRate} LOB</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Earnings Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Total Earned
                      </span>
                      <DollarSign className="w-5 h-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {earnings.totalEarned || 0} LOB
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Available
                      </span>
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {earnings.available || 0} LOB
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Withdrawn
                      </span>
                      <BarChart3 className="w-5 h-5 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {earnings.withdrawn || 0} LOB
                    </p>
                  </div>
                </div>

                {/* Withdraw Button */}
                {(earnings.available || 0) > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Withdraw Earnings
                    </h3>
                    <div className="flex items-center space-x-4">
                      <input
                        type="number"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        placeholder="Enter amount"
                        min="0"
                        max={earnings.available || 0}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={() => setShowWithdrawModal(true)}
                        disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Withdraw
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Available: {earnings.available || 0} LOB
                    </p>
                  </div>
                )}

                {/* Earnings by Blog */}
                {earnings.earningsByBlog && earnings.earningsByBlog.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Earnings by Blog
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Blog Title
                            </th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Total Earned
                            </th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Available
                            </th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                              Withdrawn
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {earnings.earningsByBlog.map((earning) => (
                            <tr
                              key={earning.blogId}
                              className="border-b border-gray-200 dark:border-gray-700"
                            >
                              <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                                {earning.title}
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                                {earning.totalEarned || 0} LOB
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                                {earning.available || 0} LOB
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                                {earning.withdrawn || 0} LOB
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div>
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Earnings Rates Display */}
                {stats.config && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                      Current Earning Rates
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-800 dark:text-blue-300">
                      <div>
                        <span className="font-medium">{stats.config.viewsThreshold} views</span> = <span className="font-semibold">{stats.config.viewsRate} LOB</span>
                      </div>
                      <div>
                        <span className="font-medium">{stats.config.impressionsThreshold} impressions</span> = <span className="font-semibold">{stats.config.impressionsRate} LOB</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Blogs
                    </span>
                    <FileText className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.totalBlogs || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {stats.publishedBlogs || 0} published
                  </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Views
                    </span>
                    <Eye className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.totalViews || 0}
                  </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Impressions
                    </span>
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.totalImpressions || 0}
                  </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Likes
                    </span>
                    <Users className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.totalLikes || 0}
                  </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Earned
                    </span>
                    <DollarSign className="w-5 h-5 text-yellow-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.totalEarned || 0} LOB
                  </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Followers
                    </span>
                    <Users className="w-5 h-5 text-indigo-500" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stats.followers || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Following {stats.following || 0}
                  </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, blogId: null })}
        onConfirm={handleConfirmDelete}
        title="Delete Blog"
        message="Are you sure you want to delete this blog? This action cannot be undone."
        confirmText={deleteBlogMutation.isLoading ? 'Deleting...' : 'Delete'}
        variant="danger"
      />

      {/* Withdraw Confirmation Modal */}
      <ConfirmModal
        isOpen={showWithdrawModal}
        onClose={() => {
          setShowWithdrawModal(false);
          setWithdrawAmount('');
        }}
        onConfirm={handleWithdraw}
        title="Withdraw Earnings"
        message={`Are you sure you want to withdraw ${withdrawAmount} LOB tokens? This will be added to your wallet balance.`}
        confirmText={withdrawMutation.isLoading ? 'Withdrawing...' : 'Withdraw'}
      />
    </div>
  );
};

export default BloggerPage;
